// @ts-check

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import { recordUndo } from './history_service.js?v=20260903c';
import {
  PLAN_LABEL_SCALE_MODES,
  resolvePlanLabelMetrics
} from '../render/plan_label_options.js?v=20260828b';
import { isPrintPlanCaptureInProgress } from '../print/print_plan_context.js?v=20260826b';

const TEXT_ANNOTATION_TOOL = 'text_annotation';
const DEFAULT_WIDTH = 240;
const DEFAULT_HEIGHT = 90;
const MIN_WIDTH = 60;
const MIN_HEIGHT = 34;
const TEXT_ANNOTATION_REFERENCE_FONT_PX = 12;
const TEXT_LINE_HEIGHT_RATIO = 15 / TEXT_ANNOTATION_REFERENCE_FONT_PX;
const TEXT_CHAR_WIDTH_RATIO = 7.2 / TEXT_ANNOTATION_REFERENCE_FONT_PX;
const TEXT_FONT_FAMILY = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const TEXT_TAB_SIZE = 4;
const INITIAL_FOCUS_GRACE_MS = 650;
const DRAG_START_TOLERANCE_PX = 3;

let textMeasureContext = null;

function numeric(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function ensureProjectTextAnnotations(project = S.project) {
  if (!project || typeof project !== 'object') return [];
  project.textAnnotations = Array.isArray(project.textAnnotations)
    ? project.textAnnotations.map(normalizeTextAnnotation).filter(Boolean)
    : [];
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (S.selection.textAnnotationId && !project.textAnnotations.some(annotation => annotation.id === S.selection.textAnnotationId)) {
    S.selection.textAnnotationId = null;
  }
  return project.textAnnotations;
}

export function normalizeTextAnnotation(raw = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const x = numeric(raw.x);
  const z = numeric(raw.z);
  const width = Math.max(MIN_WIDTH, numeric(raw.width, DEFAULT_WIDTH));
  const height = Math.max(MIN_HEIGHT, numeric(raw.height, DEFAULT_HEIGHT));
  return {
    id: String(raw.id || makeId('textAnnotation')),
    type: 'text-annotation',
    x,
    z,
    width,
    height,
    text: typeof raw.text === 'string' ? raw.text : '',
    createdAt: numeric(raw.createdAt, Date.now()),
    updatedAt: numeric(raw.updatedAt, Date.now())
  };
}

export function normalizeProjectTextAnnotations(project = S.project) {
  return ensureProjectTextAnnotations(project);
}

function setStatusText(text) {
  const el = document.getElementById('statusText') || document.getElementById('statusbar');
  if (el) el.textContent = text;
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.statusText = text;
}

function pointerWorld(event) {
  const svg = document.getElementById('planSvg');
  const rect = svg?.getBoundingClientRect?.();
  if (!rect) return null;
  const scale = numeric(S.camera2D?.scale, 1) || 1;
  return {
    x: (event.clientX - rect.left - rect.width / 2) / scale + numeric(S.camera2D?.x),
    z: (event.clientY - rect.top - rect.height / 2) / scale + numeric(S.camera2D?.z)
  };
}

function selectedTextAnnotationId() {
  return S.selection?.textAnnotationId || null;
}

function editingTextAnnotationId() {
  const id = S.ui?.textAnnotationEditingId || null;
  return id && selectedTextAnnotationId() === id ? id : null;
}

function findTextAnnotation(id) {
  const target = String(id || '').trim();
  return ensureProjectTextAnnotations().find(annotation => annotation.id === target) || null;
}

function updateToolbarButton() {
  const button = document.getElementById('btnTextAnnotationTool');
  if (!button) return;
  const active = isTextAnnotationToolActive();
  button.classList.toggle('is-active', active);
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
}

export function syncTextAnnotationToolbarButton() {
  updateToolbarButton();
}

export function isTextAnnotationToolActive() {
  return S.ui?.activeTool?.owner === 'core' && S.ui.activeTool.tool === TEXT_ANNOTATION_TOOL;
}

export function activateTextAnnotationTool() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.activeTool = { owner: 'core', tool: TEXT_ANNOTATION_TOOL, meta: {} };
  S.ui.textAnnotationGhost = null;
  updateToolbarButton();
  setStatusText('Textfeld · Position im Plan anklicken');
  AppRuntime.requestPlanRender('text-annotation-tool-on');
  return true;
}

export function deactivateTextAnnotationTool() {
  if (isTextAnnotationToolActive()) S.ui.activeTool = null;
  if (S.ui?.textAnnotationGhost) S.ui.textAnnotationGhost = null;
  pruneEmptyTextAnnotations();
  updateToolbarButton();
  setStatusText('Textfeld deaktiviert');
  AppRuntime.requestPlanRender('text-annotation-tool-off');
  return true;
}

export function cancelTextAnnotationInteraction() {
  if (S.drag?.active && S.drag.type === 'textAnnotation') {
    S.drag.active = false;
    S.drag.type = null;
    pruneEmptyTextAnnotations();
    return true;
  }
  if (isTextAnnotationToolActive()) {
    deactivateTextAnnotationTool();
    return true;
  }
  if (S.selection?.textAnnotationId) {
    S.selection.textAnnotationId = null;
    pruneEmptyTextAnnotations();
    return true;
  }
  return false;
}

function selectTextAnnotation(id) {
  if (!findTextAnnotation(id)) return false;
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  const selectionChanged = S.selection.textAnnotationId !== id;
  S.selection.textAnnotationId = id;
  S.selection.dimensionId = null;
  S.selection.areaMeasurementId = null;
  S.ui.textAnnotationEditingId = null;
  AppRuntime.clearMultiSelection();
  setStatusText('Textfeld markiert');
  if (selectionChanged) AppRuntime.requestPlanRender('text-annotation-select');
  return true;
}

function editTextAnnotation(id, options = {}) {
  if (!findTextAnnotation(id)) return false;
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.selection.textAnnotationId = id;
  S.selection.dimensionId = null;
  S.selection.areaMeasurementId = null;
  S.ui.textAnnotationEditingId = id;
  S.ui.textAnnotationFocusId = id;
  if (options.grace === true) {
    S.ui.textAnnotationFocusGraceUntil = Date.now() + INITIAL_FOCUS_GRACE_MS;
  }
  AppRuntime.clearMultiSelection();
  setStatusText('Textfeld bearbeiten');
  AppRuntime.requestPlanRender('text-annotation-edit');
  return true;
}

export function clearTextAnnotationSelection() {
  pruneEmptyTextAnnotations();
  if (!S.selection?.textAnnotationId) return false;
  if (S.ui?.textAnnotationEditingId === S.selection.textAnnotationId) S.ui.textAnnotationEditingId = null;
  S.selection.textAnnotationId = null;
  return true;
}

function deleteTextAnnotationById(id, options = {}) {
  const target = String(id || '').trim();
  if (!target) return false;
  const annotations = ensureProjectTextAnnotations();
  const index = annotations.findIndex(annotation => annotation.id === target);
  if (index < 0) return false;
  if (options.recordUndo === true) recordUndo('Textfeld loeschen');
  annotations.splice(index, 1);
  if (S.selection?.textAnnotationId === target) S.selection.textAnnotationId = null;
  if (S.ui?.textAnnotationEditingId === target) S.ui.textAnnotationEditingId = null;
  if (S.ui?.textAnnotationFocusId === target) S.ui.textAnnotationFocusId = null;
  if (S.file) S.file.dirty = true;
  return true;
}

function textContentValue(value) {
  return String(value || '').replace(/\u00a0/g, ' ').replace(/\r\n?/g, '\n');
}

function textAnnotationLines(value) {
  const text = textContentValue(value);
  return text ? text.split('\n') : [''];
}

function displayTextAnnotationLine(line) {
  return String(line || '').replace(/\t/g, ' '.repeat(TEXT_TAB_SIZE));
}

function textAnnotationPlanLabelMetrics() {
  return resolvePlanLabelMetrics({
    cameraScale: S.camera2D?.scale,
    scaleMode: isPrintPlanCaptureInProgress()
      ? PLAN_LABEL_SCALE_MODES.SCREEN
      : PLAN_LABEL_SCALE_MODES.MODEL,
    screenFontSizePx: TEXT_ANNOTATION_REFERENCE_FONT_PX
  });
}

function textAnnotationFontString(fontSizePx) {
  return `800 ${fontSizePx}px ${TEXT_FONT_FAMILY}`;
}

function textAnnotationMeasureContext() {
  if (textMeasureContext) return textMeasureContext;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  textMeasureContext = canvas.getContext('2d');
  return textMeasureContext;
}

function measureTextAnnotationLineWidth(line, fontSizePx) {
  const displayLine = displayTextAnnotationLine(line);
  const ctx = textAnnotationMeasureContext();
  if (!ctx) return displayLine.length * TEXT_CHAR_WIDTH_RATIO * fontSizePx;
  ctx.font = textAnnotationFontString(fontSizePx);
  return ctx.measureText(displayLine || ' ').width;
}

function measureTextAnnotationText(value, fontSizePx) {
  const lines = textAnnotationLines(value);
  const hasText = textContentValue(value).length > 0;
  const longestLineWidth = Math.max(0, ...lines.map(line => measureTextAnnotationLineWidth(line, fontSizePx)));
  const lineHeight = TEXT_LINE_HEIGHT_RATIO * fontSizePx;
  return {
    lines,
    width: hasText ? Math.max(1, Math.ceil(longestLineWidth)) : MIN_WIDTH,
    height: hasText ? Math.max(lineHeight, Math.ceil(lines.length * lineHeight)) : MIN_HEIGHT
  };
}

function textAnnotationContentSize(annotation, fontSizePx) {
  const measured = measureTextAnnotationText(annotation?.text, fontSizePx);
  return {
    ...measured,
    width: measured.width,
    height: measured.height
  };
}

function syncTextAnnotationSize(annotation, fontSizePx) {
  if (!annotation) return null;
  const size = textAnnotationContentSize(annotation, fontSizePx);
  annotation.width = size.width;
  annotation.height = size.height;
  return size;
}

function textAnnotationIsEmpty(annotation) {
  return !textContentValue(annotation?.text).trim();
}

function pruneEmptyTextAnnotations() {
  const annotations = ensureProjectTextAnnotations();
  let removed = false;
  for (let index = annotations.length - 1; index >= 0; index -= 1) {
    if (!textAnnotationIsEmpty(annotations[index])) continue;
    if (S.selection?.textAnnotationId === annotations[index].id) S.selection.textAnnotationId = null;
    annotations.splice(index, 1);
    removed = true;
  }
  if (removed && S.file) S.file.dirty = true;
  return removed;
}

export function deleteSelectedTextAnnotation() {
  const id = selectedTextAnnotationId();
  if (!id) return false;
  if (!deleteTextAnnotationById(id, { recordUndo: true })) return false;
  setStatusText('Textfeld gelöscht');
  AppRuntime.requestAppRefresh(false, 'text-annotation-delete');
  return true;
}

export function nudgeSelectedTextAnnotation(dx, dz) {
  const annotation = findTextAnnotation(selectedTextAnnotationId());
  if (!annotation) return false;
  recordUndo('Textfeld verschieben');
  annotation.x += numeric(dx);
  annotation.z += numeric(dz);
  annotation.updatedAt = Date.now();
  if (S.file) S.file.dirty = true;
  AppRuntime.requestAppRefresh(false, 'text-annotation-nudge');
  return true;
}

function createTextAnnotation(world) {
  const annotations = ensureProjectTextAnnotations();
  const annotation = normalizeTextAnnotation({
    x: numeric(world?.x),
    z: numeric(world?.z),
    width: MIN_WIDTH,
    height: MIN_HEIGHT,
    text: '',
    createdAt: Date.now(),
    updatedAt: Date.now()
  });
  recordUndo('Textfeld setzen');
  annotations.push(annotation);
  S.selection.textAnnotationId = annotation.id;
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.textAnnotationGhost = null;
  S.ui.textAnnotationEditingId = annotation.id;
  S.ui.textAnnotationFocusId = annotation.id;
  S.ui.textAnnotationFocusGraceUntil = Date.now() + INITIAL_FOCUS_GRACE_MS;
  if (isTextAnnotationToolActive()) {
    S.ui.activeTool = null;
    updateToolbarButton();
  }
  if (S.file) S.file.dirty = true;
  setStatusText('Textfeld bearbeiten');
  AppRuntime.requestAppRefresh(false, 'text-annotation-create');
  return annotation;
}

function startTextAnnotationDrag(event, annotation, handle) {
  const world = pointerWorld(event);
  if (!world || !annotation) return false;
  S.drag = {
    ...(S.drag || {}),
    active: true,
    type: 'textAnnotation',
    annotationId: annotation.id,
    handle,
    startMouse: world,
    startScreen: { x: numeric(event.clientX), y: numeric(event.clientY) },
    startAnnotation: JSON.parse(JSON.stringify(annotation)),
    selectedBeforeDrag: selectedTextAnnotationId() === annotation.id,
    hasMoved: false,
    undoRecorded: false
  };
  event.preventDefault?.();
  event.stopPropagation?.();
  return true;
}

export function handleTextAnnotationPointerDown(event) {
  if (event?.button !== 0) return false;
  const editorNode = event.target?.closest?.('[data-text-annotation-editor]');
  if (editorNode) {
    const id = editorNode.getAttribute('data-text-annotation-id') || '';
    const alreadySelected = selectedTextAnnotationId() === id;
    if (findTextAnnotation(id)) {
      if (!S.selection || typeof S.selection !== 'object') S.selection = {};
      S.selection.textAnnotationId = id;
      S.selection.dimensionId = null;
      if (!alreadySelected || document.activeElement !== editorNode) {
        focusTextAnnotationEditor(id);
      }
      event.stopPropagation?.();
      return true;
    }
  }
  const annotationNode = event.target?.closest?.('[data-text-annotation-id]');
  if (annotationNode) {
    const id = annotationNode.getAttribute('data-text-annotation-id') || '';
    const annotation = findTextAnnotation(id);
    if (!annotation) return false;
    return startTextAnnotationDrag(event, annotation, 'move');
  }
  if (!isTextAnnotationToolActive()) return false;
  const world = pointerWorld(event);
  if (!world) return false;
  event.preventDefault?.();
  event.stopPropagation?.();
  createTextAnnotation(world);
  return true;
}

export function handleTextAnnotationPointerMove(event) {
  if (!S.drag?.active && focusedTextAnnotationEditor()) return false;
  const world = pointerWorld(event);
  if (!world) return false;
  if (S.drag?.active && S.drag.type === 'textAnnotation') {
    const annotation = findTextAnnotation(S.drag.annotationId);
    const start = S.drag.startAnnotation;
    if (!annotation || !start) return false;
    const screenDx = numeric(event.clientX) - numeric(S.drag.startScreen?.x);
    const screenDy = numeric(event.clientY) - numeric(S.drag.startScreen?.y);
    if (!S.drag.hasMoved && Math.hypot(screenDx, screenDy) < DRAG_START_TOLERANCE_PX) {
      return true;
    }
    if (!S.drag.undoRecorded) {
      recordUndo('Textfeld verschieben');
      S.drag.undoRecorded = true;
    }
    S.drag.hasMoved = true;
    if (S.selection?.textAnnotationId === annotation.id) S.selection.textAnnotationId = null;
    const dx = world.x - numeric(S.drag.startMouse?.x);
    const dz = world.z - numeric(S.drag.startMouse?.z);
    annotation.x = numeric(start.x) + dx;
    annotation.z = numeric(start.z) + dz;
    annotation.updatedAt = Date.now();
    if (S.file) S.file.dirty = true;
    AppRuntime.requestPlanRender('text-annotation-drag', { kind: 'annotations' });
    return true;
  }
  if (isTextAnnotationToolActive()) {
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    S.ui.textAnnotationGhost = {
      x: world.x,
      z: world.z
    };
    AppRuntime.requestPlanRender('text-annotation-preview', { kind: 'annotations' });
    return true;
  }
  return false;
}

export function handleTextAnnotationPointerUp() {
  if (!(S.drag?.active && S.drag.type === 'textAnnotation')) return false;
  const annotationId = S.drag.annotationId;
  const moved = S.drag.hasMoved === true;
  S.drag.active = false;
  S.drag.type = null;
  S.drag.annotationId = null;
  S.drag.handle = null;
  S.drag.startScreen = null;
  S.drag.startAnnotation = null;
  S.drag.selectedBeforeDrag = false;
  S.drag.hasMoved = false;
  S.drag.undoRecorded = false;
  if (moved) {
    if (annotationId && findTextAnnotation(annotationId)) {
      if (!S.selection || typeof S.selection !== 'object') S.selection = {};
      if (S.ui?.textAnnotationEditingId === annotationId) S.ui.textAnnotationEditingId = null;
      S.selection.textAnnotationId = annotationId;
    }
    setStatusText('Textfeld verschoben');
    AppRuntime.requestPlanRender('text-annotation-move-finished');
    return true;
  }
  if (annotationId && selectTextAnnotation(annotationId)) {
    if (S.ui?.textAnnotationEditingId === annotationId) S.ui.textAnnotationEditingId = null;
    setStatusText('Textfeld markiert');
    AppRuntime.requestPlanRender('text-annotation-click-select');
    return true;
  }
  setStatusText('Bereit');
  return true;
}

export function handleTextAnnotationDoubleClick(event) {
  const annotationNode = event?.target?.closest?.('[data-text-annotation-id]');
  if (!annotationNode) return false;
  const id = annotationNode.getAttribute('data-text-annotation-id') || '';
  if (!findTextAnnotation(id)) return false;
  event.preventDefault?.();
  event.stopPropagation?.();
  return editTextAnnotation(id);
}

export function wantsTextAnnotationPointerMoveFrame() {
  if (!S.drag?.active && focusedTextAnnotationEditor()) return false;
  return isTextAnnotationToolActive() || S.drag?.type === 'textAnnotation';
}

function screenTextRect(annotation, worldToScreen, fontSizePx) {
  const center = worldToScreen(annotation.x, annotation.z);
  const size = textAnnotationContentSize(annotation, fontSizePx);
  return {
    ...size,
    center,
    x: center.x - size.width / 2,
    y: center.y - size.height / 2
  };
}

function svgEl(ctx, tag, attrs, parent) {
  return ctx.svgEl(tag, attrs, parent);
}

function textAnnotationEditorElement(id) {
  if (!id) return null;
  const safeId = String(id).replace(/"/g, '\\"');
  return document.querySelector(`[data-text-annotation-editor][data-text-annotation-id="${safeId}"]`);
}

function focusedTextAnnotationEditor() {
  return document.activeElement?.closest?.('[data-text-annotation-editor]') || null;
}

function insertTextAtTextareaSelection(editor, value) {
  if (!(editor instanceof HTMLTextAreaElement)) return false;
  const start = numeric(editor.selectionStart);
  const end = numeric(editor.selectionEnd, start);
  const before = editor.value.slice(0, start);
  const after = editor.value.slice(end);
  editor.value = `${before}${value}${after}`;
  const caret = start + value.length;
  editor.setSelectionRange(caret, caret);
  editor.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

function focusTextAnnotationEditor(id, options = {}) {
  const delay = Math.max(0, numeric(options.delayMs, 0));
  const focus = attempts => {
    const editor = textAnnotationEditorElement(id);
    if (!editor) {
      if (attempts > 0) window.setTimeout(() => focus(attempts - 1), 35);
      return;
    }
    editor.focus?.({ preventScroll: true });
    if (
      editor instanceof HTMLTextAreaElement
      || editor instanceof HTMLInputElement
    ) {
      const end = String(editor.value || '').length;
      editor.setSelectionRange?.(end, end);
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };
  window.setTimeout(() => focus(12), delay);
}

function applyTextAnnotationEditorRect(foreign, editor, annotation, worldToScreen, fontSizePx) {
  const rect = screenTextRect(annotation, worldToScreen, fontSizePx);
  foreign.setAttribute('x', String(rect.x));
  foreign.setAttribute('y', String(rect.y));
  foreign.setAttribute('width', String(rect.width));
  foreign.setAttribute('height', String(rect.height));
  editor.style.width = `${rect.width}px`;
  editor.style.height = `${rect.height}px`;
  editor.style.fontSize = `${fontSizePx}px`;
  editor.style.lineHeight = `${TEXT_LINE_HEIGHT_RATIO * fontSizePx}px`;
  return rect;
}

function renderTextAnnotation(parent, annotation, ctx, options = {}) {
  const selected = options.selected === true;
  const editing = selected && editingTextAnnotationId() === annotation.id;
  const labelMetrics = ctx.labelMetrics || textAnnotationPlanLabelMetrics();
  const fontSizePx = labelMetrics.fontSizePx;
  const rect = screenTextRect(annotation, ctx.worldToScreen, fontSizePx);
  const group = svgEl(ctx, 'g', {
    class: `text-annotation${selected ? ' is-selected' : ''}${editing ? ' is-editing' : ''}${options.preview ? ' is-preview' : ''}`,
    'data-text-annotation-id': annotation.id || '',
    'data-plan-label-size': labelMetrics.size,
    'data-plan-label-scale-mode': labelMetrics.scaleMode
  }, parent);
  if (options.preview) {
    const center = ctx.worldToScreen(annotation.x, annotation.z);
    svgEl(ctx, 'line', {
      class: 'text-annotation-placement-caret',
      x1: center.x,
      y1: center.y - 10,
      x2: center.x,
      y2: center.y + 10
    }, group);
    return;
  }
  svgEl(ctx, 'rect', {
    class: 'text-annotation-box',
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    'data-text-annotation-id': annotation.id || ''
  }, group);
  if (!editing && ctx.vectorText === true) {
    // Captures remove HTML/foreignObject nodes. Emit the same measured lines as
    // standalone SVG text so text annotations survive print/BOM serialization.
    const text = svgEl(ctx, 'text', {
      x: rect.x, y: rect.y, fill: '#111827', 'font-family': TEXT_FONT_FAMILY,
      'font-size': fontSizePx, 'font-weight': 800, 'dominant-baseline': 'text-before-edge',
      style: 'white-space:pre', 'data-text-annotation-id': annotation.id || ''
    }, group);
    rect.lines.forEach((line, index) => {
      svgEl(ctx, 'tspan', { x: rect.x, y: rect.y + index * TEXT_LINE_HEIGHT_RATIO * fontSizePx }, text)
        .textContent = displayTextAnnotationLine(line);
    });
    return;
  }
  if (!editing) {
    const foreign = svgEl(ctx, 'foreignObject', {
      class: 'text-annotation-foreign',
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      'data-text-annotation-id': annotation.id || ''
    }, group);
    const display = document.createElement('div');
    display.className = 'text-annotation-display';
    display.dataset.textAnnotationId = annotation.id || '';
    display.style.fontSize = `${fontSizePx}px`;
    display.style.lineHeight = `${TEXT_LINE_HEIGHT_RATIO * fontSizePx}px`;
    display.textContent = annotation.text || '';
    foreign.appendChild(display);
    return;
  }
  const foreign = svgEl(ctx, 'foreignObject', {
    class: 'text-annotation-foreign',
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    'data-text-annotation-id': annotation.id || ''
  }, group);
  const editor = document.createElement('textarea');
  editor.className = 'text-annotation-editor';
  editor.dataset.textAnnotationEditor = 'true';
  editor.dataset.textAnnotationId = annotation.id || '';
  editor.setAttribute('spellcheck', 'false');
  editor.setAttribute('tabindex', selected && !options.preview ? '0' : '-1');
  editor.setAttribute('rows', String(Math.max(1, rect.lines.length)));
  editor.value = annotation.text || '';
  applyTextAnnotationEditorRect(foreign, editor, annotation, ctx.worldToScreen, fontSizePx);
  editor.addEventListener('input', () => {
    const current = findTextAnnotation(annotation.id);
    if (!current) return;
    const liveFontSizePx = textAnnotationPlanLabelMetrics().fontSizePx;
    current.text = textContentValue(editor.value);
    syncTextAnnotationSize(current, liveFontSizePx);
    applyTextAnnotationEditorRect(foreign, editor, current, ctx.worldToScreen, liveFontSizePx);
    if (current.text.trim() && S.ui?.textAnnotationFocusGraceUntil) {
      S.ui.textAnnotationFocusGraceUntil = null;
    }
    current.updatedAt = Date.now();
    if (S.file) S.file.dirty = true;
  });
  editor.addEventListener('blur', () => {
    const current = findTextAnnotation(annotation.id);
    if (!current) return;
    current.text = textContentValue(editor.value);
    syncTextAnnotationSize(current, textAnnotationPlanLabelMetrics().fontSizePx);
    if (textAnnotationIsEmpty(current)) {
      if (Date.now() < numeric(S.ui?.textAnnotationFocusGraceUntil)) {
        focusTextAnnotationEditor(current.id, { delayMs: 80 });
        return;
      }
      deleteTextAnnotationById(current.id);
      AppRuntime.requestAppRefresh(false, 'text-annotation-empty-delete');
    } else if (S.ui?.textAnnotationEditingId === current.id) {
      S.ui.textAnnotationEditingId = null;
      AppRuntime.requestPlanRender('text-annotation-edit-blur');
    }
  });
  editor.addEventListener('keydown', event => {
    if (event.key === 'Tab') {
      event.preventDefault();
      insertTextAtTextareaSelection(editor, '\t');
    } else if (event.key === 'Escape') {
      event.preventDefault();
      const current = findTextAnnotation(annotation.id);
      if (current) {
        current.text = textContentValue(editor.value);
        syncTextAnnotationSize(current, textAnnotationPlanLabelMetrics().fontSizePx);
        S.ui.textAnnotationFocusGraceUntil = null;
        if (textAnnotationIsEmpty(current)) {
          deleteTextAnnotationById(current.id);
          setStatusText('Textfeld gelöscht');
        } else if (S.selection?.textAnnotationId === current.id) {
          S.selection.textAnnotationId = null;
          S.ui.textAnnotationEditingId = null;
          setStatusText('Textfeld geschlossen');
        }
        AppRuntime.requestAppRefresh(false, 'text-annotation-escape');
      }
    }
    event.stopPropagation();
  });
  foreign.appendChild(editor);
  if (S.ui?.textAnnotationFocusId === annotation.id && selected && !options.preview) {
    S.ui.textAnnotationFocusId = null;
    focusTextAnnotationEditor(annotation.id, { delayMs: 90 });
  }
}

export function renderTextAnnotations2D(parent, ctx = {}) {
  if (!parent || typeof ctx.svgEl !== 'function' || typeof ctx.worldToScreen !== 'function') return;
  const layer = ctx.svgEl('g', { class: 'text-annotation-layer' }, parent);
  const renderCtx = { ...ctx, labelMetrics: ctx.labelMetrics || textAnnotationPlanLabelMetrics() };
  const selectedId = selectedTextAnnotationId();
  for (const annotation of ensureProjectTextAnnotations()) {
    renderTextAnnotation(layer, annotation, renderCtx, { selected: annotation.id === selectedId });
  }
  const ghost = S.ui?.textAnnotationGhost;
  if (isTextAnnotationToolActive() && ghost && !selectedId && !focusedTextAnnotationEditor()) {
    renderTextAnnotation(layer, {
      id: 'text-annotation-preview',
      x: ghost.x,
      z: ghost.z,
      width: MIN_WIDTH,
      height: MIN_HEIGHT,
      text: ''
    }, renderCtx, { preview: true });
  }
}

export {
  TEXT_ANNOTATION_TOOL
};
